import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { MainLayout } from "@/components/layout/MainLayout";
import AnalysisResults from "@/components/AnalysisResults";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { X, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const PromptScorePage = () => {
  const { scoreId } = useParams<{ scoreId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, membership, loading: authLoading } = useAuth();

  // Get result and onboarding flag from navigation state
  const state = location.state as {
    result?: any;
    fromOnboarding?: boolean;
    toolRecommendations?: any[] | null;
    recommendationId?: string | null;
    originalPrompt?: string | null;
    selectedToolName?: string | null;
  } | null;
  const [result, setResult] = useState<any>(state?.result || null);
  const [loading, setLoading] = useState(!state?.result);
  const fromOnboarding = state?.fromOnboarding ?? false;
  const [toolRecommendations, setToolRecommendations] = useState<any[] | null>(state?.toolRecommendations || null);
  const [recommendationId, setRecommendationId] = useState<string | null>(state?.recommendationId || null);
  const [originalPrompt, setOriginalPrompt] = useState<string | null>(state?.originalPrompt || null);
  const [initialSelectedTool, setInitialSelectedTool] = useState<string | null>(state?.selectedToolName || null);
  const [showBanner, setShowBanner] = useState(fromOnboarding);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Load score data from DB if no state was passed (direct URL access / refresh)
  useEffect(() => {
    if (result || !scoreId || authLoading) return;
    if (!membership?.org_id) return;

    const loadScore = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase.rpc("get_prompt_detail_for_org", {
          _org_id: membership.org_id,
          _score_id: scoreId,
        });

        if (error || !data || data.length === 0) {
          console.error("Failed to load score:", error);
          navigate("/feed", { replace: true });
          return;
        }

        const detail = data[0];

        // Parse evaluation_markdown to reconstruct the analysis
        let analysis: Record<string, unknown> = {};
        try {
          if (detail.evaluation_markdown) {
            analysis = JSON.parse(detail.evaluation_markdown);
          }
        } catch {
          // Old format or invalid JSON
        }

        const reconstructed = {
          ui: {
            ...analysis,
            score_id: detail.score_id,
            submission_id: detail.submission_id,
            is_public: detail.is_public,
            original_score: detail.original_score,
            improved_score: detail.overall_score,
            user_id: detail.user_id,
            // Ensure key fields have fallbacks
            detected_type: (analysis as any).detected_type || detail.detected_type || "Analysis",
            dimension_scores: (analysis as any).dimension_scores || detail.dimension_scores_json,
            rewrite_tagged: (analysis as any).rewrite_tagged || detail.best_prompt_extracted || "",
            rewrite_clean: (analysis as any).rewrite_clean || detail.best_prompt_extracted || "",
            original_text: (analysis as any).original_text || detail.original_content || "",
          },
          telemetry: {
            tags: (analysis as { tags?: string[] }).tags || [],
          },
        };

        setResult(reconstructed);
      } catch (err) {
        console.error("Error loading score:", err);
        navigate("/feed", { replace: true });
      } finally {
        setLoading(false);
      }
    };

    loadScore();
  }, [scoreId, result, membership?.org_id, authLoading, navigate]);

  // Load tool recommendations from DB when not already in state
  // Covers: library navigation, page refresh after loadScore, any path where result exists but tool recs don't
  useEffect(() => {
    if (toolRecommendations || !result || !scoreId || authLoading) return;

    const submissionId = result?.ui?.submission_id;

    const loadToolRecs = async () => {
      let toolRecData = null;

      // Try by submission_id
      if (submissionId) {
        const { data } = await supabase
          .from('tool_recommendations')
          .select('*')
          .eq('submission_id', submissionId)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        toolRecData = data;
      }

      // Fallback: via builder_conversations.score_id
      if (!toolRecData) {
        const { data: conv } = await supabase
          .from('builder_conversations')
          .select('id')
          .eq('score_id', scoreId)
          .limit(1)
          .maybeSingle();

        if (conv) {
          const { data } = await supabase
            .from('tool_recommendations')
            .select('*')
            .eq('conversation_id', conv.id)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();
          toolRecData = data;
        }
      }

      if (toolRecData) {
        setRecommendationId(toolRecData.id);
        setOriginalPrompt(result?.ui?.original_text || null);
        setToolRecommendations([
          { rank: 1, tool: toolRecData.recommended_tool, model: toolRecData.recommended_model || '', reasoning: toolRecData.reasoning },
          ...(toolRecData.second_tool ? [{ rank: 2, tool: toolRecData.second_tool, model: toolRecData.second_model || '', reasoning: toolRecData.second_reasoning || '' }] : []),
          ...(toolRecData.third_tool ? [{ rank: 3, tool: toolRecData.third_tool, model: toolRecData.third_model || '', reasoning: toolRecData.third_reasoning || '' }] : []),
        ]);
        if (toolRecData.user_selected_tool) {
          setInitialSelectedTool(toolRecData.user_selected_tool);
        }
      }
    };

    loadToolRecs();
  }, [result, scoreId, toolRecommendations, authLoading]);

  // Redirect if no scoreId at all
  useEffect(() => {
    if (!scoreId && !result) {
      navigate("/feed", { replace: true });
    }
  }, [scoreId, result, navigate]);

  // Handle new analysis from this page
  const handleAnalyze = async (prompt: string) => {
    navigate("/prompt", { state: { analyzePrompt: prompt } });
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="p-4 md:p-6 space-y-4">
          <Skeleton className="h-12 w-64" />
          <Skeleton className="h-96 w-full" />
        </div>
      </MainLayout>
    );
  }

  if (!result) {
    return null;
  }

  return (
    <MainLayout>
      <div className="p-4 md:p-6 space-y-4">
        {/* Build Another banner for post-onboarding */}
        {showBanner && (
          <div className="flex items-center justify-between gap-3 rounded-lg border border-primary/20 bg-primary/10 p-3">
            <span className="text-sm text-foreground">
              Ready to build your next prompt?
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="text-primary hover:text-primary/80 gap-1"
                onClick={() => navigate("/prompt")}
              >
                Start building <ArrowRight className="h-3.5 w-3.5" />
              </Button>
              <button
                onClick={() => setShowBanner(false)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        <AnalysisResults
          result={result}
          onAnalyze={handleAnalyze}
          isLoading={false}
          fromOnboarding={fromOnboarding}
          toolRecommendations={toolRecommendations}
          recommendationId={recommendationId}
          originalPrompt={originalPrompt}
          initialSelectedTool={initialSelectedTool}
        />
      </div>
    </MainLayout>
  );
};

export default PromptScorePage;
